import React from "react"
import PropTypes from "prop-types"
import theme from "styles/theme"
import PieChart from "components/charts/PieChart"
import ColumnChart from "components/charts/ColumnChart"
import ImageChart from "components/charts/ImageChart"
import { chartColors } from "tokens/colors"
import markupSuperscripts from "lib/markupSuperscripts"

// Delegates to the appropriate Chart component handler
// and provides the card/wrapper styles and footnotes.
const Chart = ({
  type,
  layout,
  title,
  subtitle,
  footnotes,
  className,
  ...props
}) => {
  const Component = {
    pieChart: PieChart,
    columnChart: ColumnChart,
    imageChart: ImageChart,
  }[type]

  const componentProps = props

  switch (type) {
    case "pieChart":
    case "columnChart":
      componentProps.options = {
        title: {
          text: markupSuperscripts(title),
          useHTML: true,
          align: "center",
          style: {
            ...theme.t5md,
            color: theme.midBlue,
          },
          margin: 30,
          widthAdjust: 0,
        },
        subtitle: {
          text: subtitle,
          align: "center",
        },
        chart: {
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          style: {
            fontFamily: theme.font,
          },
          spacing: [5, 5, 5, 5],
        },
        colors: chartColors,
        exporting: {
          chartOptions: {
            chart: { spacing: [20, 20, 20, 20] },
          },
          buttons: {
            contextButton: {
              x: 5,
              y: -6,
            },
          },
        },
        credits: {
          enabled: false,
        },
      }
      break

    case "imageChart":
      componentProps.title = title
      componentProps.subtitle = subtitle
      componentProps.layout = layout
      break
  }

  return (
    <div
      css={{
        padding: 40,
        borderBottom: `6px solid ${theme.midBlue}`,
        background: theme.white,
        gridColumn: layout === "full" && "1 / -1",
        [theme.mobile]: {
          gridColumn: "1",
          padding: "30px 20px 20px",
        },
        ".highcharts-title sup": {
          textTransform: "none",
        },
      }}
      className={className}
    >
      <Component {...componentProps} />

      {footnotes?.length > 0 && (
        <ul
          css={{
            listStyle: "none",
            margin: "30px 0 0",
            padding: 0,
            fontSize: 12,
            fontStyle: "italic",
            lineHeight: 1.6,
            color: theme.gray,
            [theme.mobile]: {
              marginTop: 20,
            },
          }}
        >
          {footnotes.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default Chart

Chart.propTypes = {
  type: PropTypes.oneOf(["pieChart", "columnChart", "imageChart"]).isRequired,
  layout: PropTypes.oneOf(["full", "half"]),
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  data: PropTypes.shape({
    rows: PropTypes.array.isRequired,
  }),
  footnotes: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
}
